import { useState } from "react";
import { Check, ChevronsUpDown, Smartphone } from "lucide-react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * Searchable device combobox.
 * - `devices`: list of device objects (need id + brand + model + optional image_url)
 * - `value`: currently-selected device id (or null)
 * - `onChange`: called with the new id (or null)
 * - `placeholder`: trigger placeholder when nothing selected
 * - `meta`: optional function (device) => string  (e.g. "from $0.00") shown beside the item
 * - `testid`: data-testid prefix; trigger gets `${testid}-trigger`, items get `${testid}-item-${id}`
 */
export default function DeviceSelect({
  devices = [],
  value = null,
  onChange,
  placeholder = "Select a device…",
  meta,
  testid = "device-select",
  emptyText = "No devices configured.",
  Icon = Smartphone,
}) {
  const [open, setOpen] = useState(false);
  const picked = devices.find((d) => d.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-3 border-2 border-slate-300 hover:border-[#0B132B] bg-white px-3 py-2.5 text-left transition"
          data-testid={`${testid}-trigger`}
        >
          {picked ? (
            <span className="flex items-center gap-3 min-w-0">
              {picked.image_url ? (
                <img src={picked.image_url} alt="" className="w-8 h-8 object-cover bg-slate-100 flex-shrink-0" />
              ) : (
                <span className="w-8 h-8 bg-slate-100 grid place-items-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-slate-400" />
                </span>
              )}
              <span className="min-w-0">
                <span className="tw-overline block leading-none">{picked.brand}</span>
                <span className="font-bold text-[#0B132B] text-sm truncate block">{picked.model}</span>
              </span>
            </span>
          ) : (
            <span className="text-slate-500 text-sm">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[480px]" align="start">
        <Command
          filter={(value, search) => {
            // Match against the searchable key we set on each item below.
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search devices…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{devices.length === 0 ? emptyText : "No matching devices."}</CommandEmpty>
            <CommandGroup>
              {devices.map((d) => {
                const sel = d.id === value;
                const label = `${d.brand} ${d.model}`;
                return (
                  <CommandItem
                    key={d.id}
                    value={label}
                    onSelect={() => {
                      onChange(d.id === value ? null : d.id);
                      setOpen(false);
                    }}
                    data-testid={`${testid}-item-${d.id}`}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    {d.image_url ? (
                      <img src={d.image_url} alt="" className="w-8 h-8 object-cover bg-slate-100 flex-shrink-0" />
                    ) : (
                      <span className="w-8 h-8 bg-slate-100 grid place-items-center flex-shrink-0">
                        <Icon className="h-4 w-4 text-slate-400" />
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="tw-overline block leading-none">{d.brand}</span>
                      <span className="font-bold text-[#0B132B] text-sm truncate block">{d.model}</span>
                    </span>
                    {meta && <span className="mono text-xs text-slate-500 flex-shrink-0">{meta(d)}</span>}
                    <Check className={cn("h-4 w-4 text-[#00E5FF] flex-shrink-0", sel ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
